/**
 * DeletedInSageBadge — small inline pill shown on an order row when the
 * backend has detected that the linked Sage production sales order has
 * disappeared from `SO_SalesOrderHeader` (via the
 * `detect_sage_sales_order_deletes` Celery beat).
 *
 * The badge is informational, not actionable — hover reveals a tooltip
 * explaining the detection and when it was first seen. Once the order
 * has been invoiced and matched against `sales_history` (or the row
 * reappears), the flag clears on the next sync pass.
 */

import { useState } from 'react';
import styles from './DeletedInSageBadge.module.css';

export interface DeletedInSageBadgeProps {
  /** When the sync first noticed the row was missing from MySQL. */
  detectedAt?: string | null;
}

function formatDetected(detectedAt: string | null | undefined): string {
  if (!detectedAt) return 'Detection time unknown';
  try {
    const d = new Date(detectedAt);
    return `First detected: ${d.toLocaleString()}`;
  } catch {
    return 'Detection time unknown';
  }
}

const DeletedInSageBadge: React.FC<DeletedInSageBadgeProps> = ({
  detectedAt,
}) => {
  const [hover, setHover] = useState(false);

  return (
    <span
      className={styles.badge}
      role="status"
      aria-label="Sage sales order no longer present in production"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      tabIndex={0}
    >
      <span className={styles.icon} aria-hidden="true">⚠</span>
      Deleted in Sage
      {hover && (
        <span className={styles.tooltip} role="tooltip">
          <span className={styles.tooltipTitle}>
            Missing from Sage production
          </span>
          The linked Sage sales order is no longer in
          {' '}
          <code>SO_SalesOrderHeader</code>. This usually means the order
          shipped and moved to invoice history — but if it was submitted
          recently and never made it to invoicing, it needs investigation.
          <span className={styles.tooltipDetected}>
            {formatDetected(detectedAt)}
          </span>
        </span>
      )}
    </span>
  );
};

export default DeletedInSageBadge;
